import { PostgrestResponse } from '@supabase/supabase-js';
import { IVwExtratoMovimentacoesAluno } from '../../../types/views/IVwExtratoMovimentacoesAluno';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * @name VwExtratoMovimentacoesAlunoService
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Service para extrato de movimentações financeiras dos alunos - histórico de transações
 * @package src/services/supabase/views/VwExtratoMovimentacoesAlunoService.ts
 */
export class VwExtratoMovimentacoesAlunoService extends BaseMultiTenantService<IVwExtratoMovimentacoesAluno> {
     constructor() {
          super('vw_extrato_movimentacoes_aluno', [], 'fk_id_organizacao');
     }

     /**
      * Lista todo o extrato de movimentações de um aluno específico
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @param limit Limite de registros (default: 50)
      * @returns Histórico de movimentações do aluno
      */
     async getExtratoAluno(
          organizationId: number, 
          alunoId: number, 
          limit: number = 50
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .order('id')
               .limit(limit);
     }

     /**
      * Lista movimentações por período
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @param dataInicio Data de início do período
      * @param dataFim Data de fim do período
      * @returns Histórico de movimentações no período
      */
     async getMovimentacoesPorPeriodo(
          organizationId: number,
          alunoId: number,
          dataInicio: string,
          dataFim: string
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .gte('data_movimentacao', dataInicio)
               .lte('data_movimentacao', dataFim)
               .order('data_movimentacao', { ascending: false });
     }

     /**
      * Lista apenas movimentações de crédito (entradas)
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @param limit Limite de registros (default: 25)
      * @returns Movimentações de crédito
      */
     async getMovimentacoesCredito(
          organizationId: number,
          alunoId: number,
          limit: number = 25
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .eq('tipo_movimentacao', 'credito')
               .order('data_movimentacao', { ascending: false })
               .limit(limit);
     }

     /**
      * Lista apenas movimentações de débito (saídas)
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @param limit Limite de registros (default: 25)
      * @returns Movimentações de débito
      */
     async getMovimentacoesDebito(
          organizationId: number,
          alunoId: number,
          limit: number = 25
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .eq('tipo_movimentacao', 'debito')
               .order('data_movimentacao', { ascending: false })
               .limit(limit);
     }

     /**
      * Lista movimentações do dia atual
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @returns Movimentações realizadas hoje
      */
     async getMovimentacoesHoje(
          organizationId: number,
          alunoId: number
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno[]>> {
          const hoje = new Date().toISOString().split('T')[0];
          
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .gte('data_movimentacao', `${hoje} 00:00:00`)
               .lt('data_movimentacao', `${hoje} 23:59:59`)
               .order('data_movimentacao', { ascending: false });
     }

     /**
      * Lista movimentações por tipo de origem
      * @param organizationId ID da organização
      * @param alunoId ID do aluno
      * @param tipoOrigemFilter Tipo de origem para filtrar
      * @param limit Limite de registros (default: 25)
      * @returns Movimentações do tipo específico
      */
     async getMovimentacoesPorTipo(
          organizationId: number,
          alunoId: number,
          tipoOrigemFilter: 'compra' | 'transferencia' | 'deposito' | 'estorno' | 'ajuste' | 'credito_manual' | 'debito_manual',
          limit: number = 25
     ): Promise<PostgrestResponse<IVwExtratoMovimentacoesAluno[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .eq('tipo_origem', tipoOrigemFilter)
               .order('data_movimentacao', { ascending: false })
               .limit(limit);
     }
}

export const vwExtratoMovimentacoesAlunoService = new VwExtratoMovimentacoesAlunoService();
